import React, { useRef, useState } from "react";
import ReportEditor from "./ReportEditor";
import { emitToast } from "./Toast";
import { jsonHeaders } from "./csrf";

// PCからの出勤/退勤ボタン。ホームのページ見出し（挨拶）行の右に置く＝カードの枠は持たず
// ボタン主体（申請中バナー等の帯と見た目がかぶらないようにする）。本人特定はマイページと
// 同じセッション認証（Cookie）＝ /api/employee/self/clock-in|clock-out。
//
// 日付をまたぐ未退勤も含め、状態は home API の today.status を「勤務中かどうか」だけに落とす:
//   ・勤務中(working)          → 小さな「勤務中」表示＋「退勤して日報を書く」
//   ・それ以外(未出勤/退勤済)   → 「出勤する」（退勤後もそのまま出勤ボタンへ戻す）
// 同日の再出勤は許可（中抜け）＝既存LINE打刻と同じ。打刻後は onDone() で home を再取得。
// 成否・エラーはトーストで通知（ヘッダーにインラインのエラー箱を置かない）。
//
// 勤務地（事務所/在宅）は LINE 打刻と同じく位置情報で自動判定するため、出勤時に位置情報を
// 必須で取得する（ユーザー方針）。取得できなければ出勤させない。退勤は位置を使わず日報のみ。

const API_URL = process.env.REACT_APP_API_URL || "";
const IS_DEMO = process.env.REACT_APP_DEMO === "1";

// ブラウザ位置情報を Promise で取得。デモは実際の許可を求めず即ダミーを返す（クリックデモを妨げない）。
// 本番は許可必須：拒否/失敗/タイムアウトは reject（呼び出し側で出勤をブロック）。
function getPosition() {
  if (IS_DEMO) return Promise.resolve({ lat: 35.681, lng: 139.767 });
  if (!navigator.geolocation) return Promise.reject(new Error("この端末では位置情報を取得できません。"));
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      (err) => reject(new Error(
        err.code === err.PERMISSION_DENIED
          ? "位置情報の利用が許可されていません。ブラウザの設定で許可してください。"
          : "位置情報を取得できませんでした。電波の良い場所で再度お試しください。"
      )),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  });
}

export default function PunchCard({ today, onDone }) {
  const [busy, setBusy] = useState(false);          // 出勤/退勤の送信中（多重打刻防止）
  const [editorOpen, setEditorOpen] = useState(false); // 退勤時の日報エディタ

  const submitting = useRef(false);
  const targetId = useRef(null);

  const working = today?.status === "working";

  const post = (path, payload) =>
    fetch(`${API_URL}${path}`, {
      method: "POST",
      credentials: "include",
      headers: jsonHeaders(),
      body: JSON.stringify(payload || {}),
    });

  // 出勤：位置情報を取得してから打刻。位置が取れなければ打刻しない（必須方針）。
  const clockIn = async () => {
    if (submitting.current) return;
    submitting.current = true;
    setBusy(true);
    try {
      const pos = await getPosition();
      const res = await post("/api/employee/self/clock-in/", { lat: pos.lat, lng: pos.lng });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.message || "出勤打刻に失敗しました。");
      }
      emitToast("出勤しました");
      if (onDone) await onDone();
    } catch (e) {
      emitToast(e.message || "出勤打刻に失敗しました。", "error");
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  };

  // 退勤：日報エディタで本文を確定してから打刻（日報必須）。
  const clockOut = async (report) => {
    if (submitting.current) return;
    submitting.current = true;
    setBusy(true);
    try {
      const res = await post("/api/employee/self/clock-out/", { daily_report: report, attendance_id: targetId.current });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.message || "退勤打刻に失敗しました。");
      }
      setEditorOpen(false);
      emitToast("退勤しました。お疲れさまでした");
      if (onDone) await onDone();
    } catch (e) {
      emitToast(e.message || "退勤打刻に失敗しました。", "error");
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  };

  return (
    <div className="mp-punch">
      {working ? (
        <>
          {today.clock_in_date && <span>{today.clock_in_date} 出勤</span>}
          <button className="mp-btn mp-primary" onClick={() => { targetId.current = today.attendance_id; setEditorOpen(true); }} disabled={busy}>退勤</button>
        </>
      ) : (
        <button className="mp-btn mp-accent" onClick={clockIn} disabled={busy}>出勤</button>
      )}

      {/* 退勤時の日報入力。開くたびに新規インスタンスにしたいので key を open に紐づける。 */}
      <ReportEditor
        key={editorOpen ? "open" : "closed"}
        open={editorOpen}
        title="退勤して日報を提出"
        initialText=""
        submitLabel="退勤する"
        busy={busy}
        onCancel={() => { if (!busy) setEditorOpen(false); }}
        onSubmit={clockOut}
      />
    </div>
  );
}
